import { GameplayInfo } from '@longtail-education/superprep-data-model-frontend'
import { log } from '@nx-superprep/utils'

/**
 * Gameplay names
 */
export const enum GameplayName {
  Audio001 = 'AudioWithChoicesGameplay001', // gameplay 1 - mode 1, 3
  Audio002 = 'AudioWithChoicesGameplay002', // gameplay 1 - mode 2
  ComposeWithLanguageNumbers = 'ComposeTheBlankWithLanguageNumberChoicesGameplay001',
  Diagram001 = 'DiagramGameplay001',
  Diagram002 = 'DiagramGameplay002',
  FillInWithNumber001 = 'FillInWithNumberChoicesGameplay001', // gameplay 1 - no mode
  FillInWithPeriodicTable001 = 'FillInWithPeriodicTableChoicesGameplay001',
  FillInWithLanguageNumber001 = 'FillInWithLanguageNumberChoicesGameplay001',
  FillTheBlanks001 = 'FillTheBlankWithMultipleChoiceSelectionGameplay001',
  FillInTheBlanksLetter001 = 'FillInTheBlankWithLetterChoicesGameplay001',
  FourImages001 = 'FourImageChoicesGameplay001', // gameplay 6 - mode 1
  FourImages002 = 'FourImageChoicesGameplay002', // gameplay 6 - mode 2
  FourImages003 = 'FourImageChoicesGameplay003', // gameplay 6 - mode 3
  FourImages004 = 'FourImageChoicesGameplay004', // gameplay 6 - mode 7
  FourTexts001 = 'FourTextChoicesGameplay001', // gameplay 6 - mode 4
  FourTexts002 = 'FourTextChoicesGameplay002', // gameplay 6 - mode 5
  FourTexts003 = 'FourTextChoicesGameplay003', // gameplay 6 - mode 6
  FourTexts004 = 'FourTextChoicesGameplay004', // gameplay 6 - mode 8
  Image001 = 'ImageWithChoicesGameplay001', // gameplay 2 - mode 5, 6
  Image002 = 'ImageWithChoicesGameplay002', // gameplay 3 - mode 5
  Image003 = 'ImageWithChoicesGameplay003', // gameplay 2 - mode 2, 4
  Image004 = 'ImageWithChoicesGameplay004', // chemistry compound w/choices
  Image005 = 'ImageWithChoicesGameplay005', // ecology image w/background
  Image006 = 'ImageWithChoicesGameplay006', // image w/dotted question
  ImageWithImages001 = 'ImageWithImageChoicesGameplay001',
  ImageWithNumbers001 = 'ImageWithNumberChoicesGameplay001',
  MultipleChoices = 'MultipleChoiceSelectionGameplay001',
  Text001 = 'TextWithChoicesGameplay001', // gameplay 3 - mode 2, 4
  Text002 = 'TextWithChoicesGameplay002', // gameplay 3 - mode 1, 3
  Text003 = 'TextWithChoicesGameplay003', // gameplay 2 - mode 2, 4
  TextWithElements001 = 'TextWithElementChoicesGameplay001',
  TextWithImages001 = 'TextWithImageChoicesGameplay001',
  TextWithNumbers001 = 'TextWithNumberChoicesGameplay001', // gameplay 1 - no mode
  TextWithPeriodicTable001 = 'TextWithPeriodicTableChoicesGameplay001',
  TextWithPeriodicTable002 = 'TextWithPeriodicTableChoicesGameplay002',
  Video001 = 'VideoWithChoicesGameplay001',
  Video002 = 'VideoWithChoicesGameplay002',
  FourVideos001 = 'FourVideoChoicesGameplay001',
  FourVideos002 = 'FourVideoChoicesGameplay002',
  None = 'None',
}

export const GameplayComposeFullAnswer = [
  GameplayName.MultipleChoices,
  GameplayName.FillInWithNumber001,
  GameplayName.FillInTheBlanksLetter001,
  GameplayName.ComposeWithLanguageNumbers,
]

export const GameplayMultipleChoices = [
  GameplayName.MultipleChoices,
  GameplayName.FillTheBlanks001,
  GameplayName.FillInWithNumber001,
  GameplayName.FillInTheBlanksLetter001,
  GameplayName.ComposeWithLanguageNumbers,
]

export const GameplayNumbers = [
  GameplayName.ImageWithNumbers001,
  GameplayName.TextWithNumbers001,
  GameplayName.FillInWithNumber001,
  GameplayName.FillInWithLanguageNumber001,
  GameplayName.ComposeWithLanguageNumbers,
]

export const GameplaysWithTextQuestion = [
  GameplayName.Audio001,
  GameplayName.TextWithImages001,
  GameplayName.Text003,
]

export const GameplaysWithAdditionalTextQuestion = [
  GameplayName.ImageWithNumbers001,
  GameplayName.Image004
]

export const GameplaysWithAdditionalAudioQuestion = [
  GameplayName.Image003,
  GameplayName.Text003
]

export const GameplaysWithVideoQuestion = [
  GameplayName.Video001,
  GameplayName.Video002,
  GameplayName.FourVideos001,
  GameplayName.FourVideos002,
]

export const GameplaysWithAudioQuestion = [
  GameplayName.FourImages003,
  GameplayName.FourImages004,
  GameplayName.FourTexts003,
  GameplayName.FourTexts004,
  GameplayName.Image003,
  GameplayName.Text003,
  GameplayName.FillInWithLanguageNumber001,
]

export const GameplaysWithImageQuestion = [
  GameplayName.FourTexts001,
  GameplayName.FourTexts002,
]

export const GameplaysWithoutQuestionChoice = [
  GameplayName.Diagram001,
  GameplayName.Diagram002,
  GameplayName.TextWithPeriodicTable001,
  GameplayName.TextWithPeriodicTable002
]

export const GameplaysWithEndText = [
  GameplayName.Image001,
  GameplayName.TextWithElements001,
]

export const GameplayWithTranslation = [
  GameplayName.Image001,
  GameplayName.Text001,
  GameplayName.FourImages001,
  GameplayName.FourTexts001,
  GameplayName.FillTheBlanks001,
]

export const GameplayWithQuestionTextHidden = [
  //GameplayName.Image002,
  GameplayName.Image003,
  GameplayName.FourImages003,
  GameplayName.FourTexts003,
]

export const GameplayWithAnswerTextHidden = [
  GameplayName.Image001,
  GameplayName.Image002,
  GameplayName.FourImages001,
  GameplayName.FourImages002,
  GameplayName.FourTexts001,
  GameplayName.FourTexts002,
  GameplayName.FourTexts003,
  GameplayName.FourTexts004,
]

/**
 * Gameplay infos
 */
export class Gameplay {
  name: GameplayName
  choiceCount: number
  alwaysPlayPostAnswerAudios: boolean
  private _type

  constructor(options: Partial<Gameplay> | GameplayInfo = {}) {
    this.name = options.name as GameplayName
    this.choiceCount = !GameplayNumbers.includes(this.name) ? Number(options.choiceCount || 3) : 0
    this.alwaysPlayPostAnswerAudios = 'alwaysPlayPostAnswerAudios' in options && Boolean(options.alwaysPlayPostAnswerAudios)

    this._type = ((name) => {
      switch (name) {
        case GameplayName.Diagram001:
        case GameplayName.Diagram002:
          return 'diagram'
        case GameplayName.FourImages001:
        case GameplayName.FourImages002:
        case GameplayName.FourImages003:
        case GameplayName.FourImages004:
        case GameplayName.FourTexts001:
        case GameplayName.FourTexts002:
        case GameplayName.FourTexts003:
        case GameplayName.FourTexts004:
        case GameplayName.TextWithImages001:
        case GameplayName.ImageWithImages001:
        case GameplayName.FourVideos001:
        case GameplayName.FourVideos002:
          return 'tiles'
        case GameplayName.Audio001:
        case GameplayName.Audio002:
        case GameplayName.Text001:
        case GameplayName.Text002:
        case GameplayName.Text003:
        case GameplayName.Image001:
        case GameplayName.Image002:
        case GameplayName.Image003:
        case GameplayName.Image004:
        case GameplayName.Image005:
        case GameplayName.Image006:
        case GameplayName.Video001:
        case GameplayName.Video002:
        case GameplayName.TextWithElements001:
          return 'stack'
        case GameplayName.ImageWithNumbers001:
        case GameplayName.FillInWithNumber001:
        case GameplayName.TextWithNumbers001:
        case GameplayName.ComposeWithLanguageNumbers:
        case GameplayName.FillInWithLanguageNumber001:
          return 'numbers'
        case GameplayName.TextWithPeriodicTable001:
        case GameplayName.TextWithPeriodicTable002:
        case GameplayName.FillInWithPeriodicTable001:
          return 'periodic'
        case GameplayName.MultipleChoices:
        case GameplayName.FillTheBlanks001:
        case GameplayName.FillInTheBlanksLetter001:
          return 'fill'
        default:
          return 'none'
      }
    })(this.name)
  }

  get type() {
    return this._type
  }

  get shortName() {
    return this.name.replace(/Gameplay|Choices|With|Table/gm,'')
  }

  get isMultipleChoice() {
    return GameplayMultipleChoices.includes(this.name)
  }

  get shouldHaveQuestionChoice() {
    return !GameplaysWithoutQuestionChoice.includes(this.name)
  }

  get shouldHaveEndText() {
    return GameplaysWithEndText.includes(this.name)
  }
}

/**
 * convert old cardQuestion gameplay to new structure
 */
export const inferGameplayInfo = (
  gameplayID: string,
  options: {
    gameplay?: Partial<Gameplay>,
    image: string | undefined,
    choices: string | unknown[],
    layers?: string | unknown[],
    mode?: string,
    alwaysPlayPostAnswerAudios?: boolean
  }
) => {
  if (!gameplayID || gameplayID.length === 0) {
    if (options.gameplay) {
      return options.gameplay
    } else if (options.image) {
      gameplayID =
        (options.layers ?? []).length > 0
          ? GameplayName.Diagram001
          : GameplayName.Image003
    } else {
      log.debug('error: no gameplay', options)
    }
  }

  const name = gameplayName(gameplayID, options.mode??'', options.image)
  const choiceCount = options.choices.length
  const alwaysPlayPostAnswerAudios = options.alwaysPlayPostAnswerAudios

  return {
    name,
    choiceCount,
    alwaysPlayPostAnswerAudios,
  } as Partial<Gameplay>
}

export const gameplayName = (
  gameplayString: string,
  modeString: string,
  image = 'N/A',
) => {
  if (!gameplayString || gameplayString.length === 0) {
    return GameplayName.None
  }

  const gameplay = Number(gameplayString.replace(/[^0-9]/g, ''))
  const mode = Number(modeString)

  switch (gameplay) {
    case 1:
      if (mode === 1 || mode === 3) {
        return GameplayName.Audio001
      }
      if (mode === 2) {
        return GameplayName.Audio002
      }
      return GameplayName.None
    case 2:
      if (mode === 1 || mode === 3) {
        return image === 'N/A'
          ? GameplayName.Image002
          : GameplayName.Text002
      }
      if (mode === 2 || mode === 4) {
        return image === 'N/A'
          ? GameplayName.Image003
          : GameplayName.Text003
      }
      if (mode === 5 || mode === 6) {
        return image === 'N/A'
          ? GameplayName.Image001
          : GameplayName.Text001
      }
      return GameplayName.None
    case 3:
      if (mode === 1 || mode === 3) {
        return GameplayName.Text002
      }
      if (mode === 2 || mode === 4) {
        return GameplayName.Text001
      }
      if (mode === 5) {
        return GameplayName.Image002
      }
      return GameplayName.None
    case 4:
    case 5:
      if (!mode) {
        return GameplayName.Text001
      }
      return GameplayName.None
    case 6:
      if (mode === 1) {
        return GameplayName.FourImages001
      }
      if (mode === 2) {
        return GameplayName.FourImages002
      }
      if (mode === 3) {
        return GameplayName.FourImages003
      }
      if (mode === 4) {
        return GameplayName.FourTexts001
      }
      if (mode === 5) {
        return GameplayName.FourTexts002
      }
      if (mode === 6) {
        return GameplayName.FourTexts003
      }
      if (mode === 7) {
        return GameplayName.FourImages004
      }
      if (mode === 8) {
        return GameplayName.FourTexts004
      }
      return GameplayName.None
    case 7:
    case 8:
    default:
      return GameplayName.None
  }
}
